import type { UIMessage } from 'ai';
import { selectToolResult } from './select-tool-result';
import { AI_SEARCH_EVENTS, type AiSearchAnalytics } from './ai-search-analytics';
import { classifyRefusalFromAssistantText } from './refusal-classifier';

export type AssistantTurnStatus = 'submitted' | 'streaming' | 'ready' | 'error';

export type SubmitContext = {
    latencyMs: number;
    modelExtraction: string;
    promptLength: number;
};

type AssistantTurnInputs = {
    status: AssistantTurnStatus;
    previousStatus: AssistantTurnStatus;
    messages: ReadonlyArray<UIMessage>;
    sessionId: string | null;
    /** Mutable Set used to de-dupe across re-renders. The caller owns the ref. */
    trackedAssistantIds: Set<string>;
    /** Called only when ready to fire — returns submit-time context and clears it. */
    consumeSubmitContext: (sessionId: string) => SubmitContext;
    analytics: AiSearchAnalytics;
};

/**
 * Pure-ish dispatcher for the `results_shown` / `no_results` analytics events.
 * Encapsulates the rules so it can be unit-tested without a React tree:
 *  - only fires on the transition into `'ready'`
 *  - bails when no session id is available
 *  - de-dupes on assistant message id across re-renders
 *  - picks `results_shown` vs `no_results` from the tool result count
 *
 * Returns `true` when an event was dispatched, `false` otherwise. Side effects
 * are scoped to the passed-in `trackedAssistantIds` Set and the
 * `consumeSubmitContext` callback (which the caller wires up to its refs).
 */
export function dispatchAssistantTurnAnalytics(inputs: AssistantTurnInputs): boolean {
    const {
        status,
        previousStatus,
        messages,
        sessionId,
        trackedAssistantIds,
        consumeSubmitContext,
        analytics,
    } = inputs;

    if (status !== 'ready') return false;
    if (previousStatus !== 'streaming' && previousStatus !== 'submitted') return false;
    if (!sessionId) return false;

    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
    if (!lastAssistant) return false;
    if (trackedAssistantIds.has(lastAssistant.id)) return false;

    const toolResult = selectToolResult(lastAssistant);
    if (!toolResult) {
        // Assistant turn finished without a `search_activities` tool call —
        // treat it as a refusal so we don't silently drop the funnel signal.
        trackedAssistantIds.add(lastAssistant.id);
        analytics[AI_SEARCH_EVENTS.REFUSAL]({
            block_reason: classifyRefusalFromAssistantText(collectAssistantText(lastAssistant)),
            session_id: sessionId,
        });
        return true;
    }

    trackedAssistantIds.add(lastAssistant.id);
    const { latencyMs, modelExtraction, promptLength } = consumeSubmitContext(sessionId);

    if (toolResult.count === 0) {
        analytics[AI_SEARCH_EVENTS.NO_RESULTS]({
            prompt_length: promptLength,
            session_id: sessionId,
        });
        return true;
    }

    analytics[AI_SEARCH_EVENTS.RESULTS_SHOWN]({
        result_count: toolResult.count,
        model_extraction: modelExtraction,
        model_ranking: toolResult.modelRanking,
        latency_ms: latencyMs,
        session_id: sessionId,
    });
    return true;
}

function collectAssistantText(message: UIMessage): string {
    return message.parts
        .filter(p => p.type === 'text')
        .map(p => (p.type === 'text' ? p.text : ''))
        .join('');
}
